import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
    <div><div className="text-sm font-medium text-slate-800">{label}</div>{hint && <div className="text-xs text-slate-400">{hint}</div>}</div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

function Global() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/chat/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try { await api.put("/chat/admin/settings", s); toast.success("Chat settings saved — applies to live sessions"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!s) return <div className="text-slate-400 py-6">Loading…</div>;
  return (
    <div className="space-y-3 max-w-2xl">
      {!s.enabled && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Chat is globally disabled — no employee can message.</div>}
      <Toggle label="Enable Chat Module" hint="Master switch for the entire chat feature" checked={s.enabled} onChange={(v) => set("enabled", v)} testid="cs-enabled" />
      <Toggle label="Direct Messaging" hint="1:1 conversations" checked={s.dm_enabled} onChange={(v) => set("dm_enabled", v)} testid="cs-dm" />
      <Toggle label="Employee Search" hint="Allow finding colleagues to start chats" checked={s.search_enabled} onChange={(v) => set("search_enabled", v)} testid="cs-search" />
      <Toggle label="Read Receipts" checked={s.read_receipts} onChange={(v) => set("read_receipts", v)} testid="cs-receipts" />
      <Toggle label="In-app Notifications" hint="Notify recipients of new messages" checked={s.notifications} onChange={(v) => set("notifications", v)} testid="cs-notif" />
      <Toggle label="Cross-Company Messaging" hint="Off by default — allow messaging across companies" checked={s.cross_company} onChange={(v) => set("cross_company", v)} testid="cs-crosscompany" />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Max Message Length</Label><Input type="number" value={s.max_length} onChange={(e) => set("max_length", Number(e.target.value))} data-testid="cs-maxlen" /></div>
        <div className="space-y-1.5"><Label>Max Messages / Minute</Label><Input type="number" value={s.max_per_minute} onChange={(e) => set("max_per_minute", Number(e.target.value))} data-testid="cs-rate" /></div>
      </div>
      <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="cs-save">Save Global Settings</Button>
    </div>
  );
}

function Profiles() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/chat/admin/profiles").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (r) => {
    const { role, ...body } = r;
    try { await api.put(`/chat/admin/profiles/${encodeURIComponent(role)}`, body); toast.success(`${role} profile saved`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = (i, k) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: !r[k] } : r));
  const cols = [["can_access", "Access"], ["can_start", "Start"], ["can_search", "Search"], ["other_dept", "Other Dept"], ["cross_company", "Cross-Co"]];
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-500">Per-role chat permissions. Unchecking <b>Access</b> blocks the role from chat entirely.</div>
      {rows.map((r, i) => (
        <div key={r.role} className="rounded-lg border border-slate-200 p-3" data-testid={`cs-profile-${r.role}`}>
          <div className="flex items-center justify-between">
            <div className="font-medium text-slate-800 text-sm">{r.role}</div>
            <Button size="sm" variant="outline" onClick={() => save(r)} data-testid={`cs-profile-save-${r.role}`}>Save</Button>
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {cols.map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
                <Switch checked={!!r[k]} onCheckedChange={() => toggle(i, k)} /> {lbl}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const SCOPE_TYPES = ["any", "company", "department", "role", "position", "employee"];

function Policies() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", sender_scope: { type: "any", value: "" }, recipient_scope: { type: "any", value: "" },
    action: "deny", target: "both", direction: "one_way", active: true, priority: 100, start_date: "", end_date: "" };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/chat/admin/policies").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...blank, ...p, start_date: p.start_date || "", end_date: p.end_date || "" }); setOpen(true); };
  const save = async () => {
    const body = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
    try {
      if (editing) await api.put(`/chat/admin/policies/${editing.id}`, body);
      else await api.post("/chat/admin/policies", body);
      toast.success("Policy saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => { if (!window.confirm(`Delete policy "${p.name}"?`)) return; await api.delete(`/chat/admin/policies/${p.id}`); toast.success("Deleted"); load(); };
  const scopeStr = (sc) => sc.type === "any" ? "Everyone" : `${sc.type}: ${sc.value}`;
  const ScopeEditor = ({ label, val, onChange }) => (
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1.5"><Label>{label} Scope</Label>
        <Select value={val.type} onValueChange={(t) => onChange({ type: t, value: t === "any" ? "" : val.value })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SCOPE_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="space-y-1.5"><Label>Value</Label><Input disabled={val.type === "any"} value={val.value || ""} onChange={(e) => onChange({ ...val, value: e.target.value })} placeholder={val.type === "any" ? "—" : `${val.type} id/name`} /></div>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">Contact-eligibility rules. <b>Deny always wins</b>; lower priority number is evaluated first.</div>
        <Button size="sm" onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="cs-policy-new"><Icons.Plus className="h-4 w-4 mr-1.5" /> New Policy</Button>
      </div>
      <div className="space-y-2" data-testid="cs-policies">
        {rows.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No policies — defaults apply (same company allowed, cross-company off).</div>}
        {rows.map((p) => (
          <div key={p.id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between" data-testid={`cs-policy-${p.id}`}>
            <div>
              <div className="flex items-center gap-2"><span className="font-medium text-slate-800 text-sm">{p.name}</span>
                <Badge className={`${p.action === "deny" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"} hover:opacity-100 text-[10px] uppercase`}>{p.action}</Badge>
                {!p.active && <Badge className="bg-slate-100 text-slate-500 hover:opacity-100 text-[10px]">inactive</Badge>}</div>
              <div className="text-xs text-slate-500 mt-0.5">{scopeStr(p.sender_scope)} → {scopeStr(p.recipient_scope)} · {p.target} · {p.direction} · priority {p.priority}</div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}><Icons.Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => del(p)}><Icons.Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Policy" : "New Policy"}</DialogTitle><DialogDescription>Who may contact whom. Deny rules override allow rules.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cs-policy-name" /></div>
            <ScopeEditor label="Sender" val={form.sender_scope} onChange={(v) => setForm({ ...form, sender_scope: v })} />
            <ScopeEditor label="Recipient" val={form.recipient_scope} onChange={(v) => setForm({ ...form, recipient_scope: v })} />
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5"><Label>Action</Label>
                <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}><SelectTrigger data-testid="cs-policy-action"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="allow">Allow</SelectItem><SelectItem value="deny">Deny</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Applies To</Label>
                <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="both">Both</SelectItem><SelectItem value="initiate">Initiate</SelectItem><SelectItem value="message">Message</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="one_way">One-way</SelectItem><SelectItem value="mutual">Mutual</SelectItem></SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5"><Label>Priority</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} /></div>
              <div className="space-y-1.5"><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.name} className="bg-slate-900 hover:bg-slate-800" data-testid="cs-policy-save">Save Policy</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Exceptions() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", type: "suspend", value: "", reason: "", expires_at: "" });
  const load = () => api.get("/chat/admin/exceptions").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.post("/chat/admin/exceptions", { ...form, value: form.value || null, expires_at: form.expires_at || null }); toast.success("Exception applied"); setOpen(false); setForm({ user_id: "", type: "suspend", value: "", reason: "", expires_at: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (e) => { await api.delete(`/chat/admin/exceptions/${e.id}`); toast.success("Removed"); load(); };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">Per-employee overrides (suspend, disable, or block a specific contact). Each is audit-logged.</div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="cs-exc-new"><Icons.Plus className="h-4 w-4 mr-1.5" /> Add Exception</Button>
      </div>
      <div className="space-y-2" data-testid="cs-exceptions">
        {rows.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No individual exceptions.</div>}
        {rows.map((e) => (
          <div key={e.id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between" data-testid={`cs-exc-${e.id}`}>
            <div>
              <div className="flex items-center gap-2"><span className="font-medium text-slate-800 text-sm">{e.user_name}</span>
                <Badge className="bg-amber-50 text-amber-700 hover:opacity-100 text-[10px] capitalize">{e.type.replace("_", " ")}</Badge></div>
              <div className="text-xs text-slate-500 mt-0.5">{e.reason} · by {e.admin}{e.expires_at ? ` · until ${e.expires_at.slice(0, 10)}` : ""}</div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => del(e)}><Icons.Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Exception</DialogTitle><DialogDescription>Apply an individual chat restriction. A reason is required and recorded.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Employee (user)</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}><SelectTrigger data-testid="cs-exc-user"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="cs-exc-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="suspend">Suspend messaging</SelectItem><SelectItem value="disable">Disable chat</SelectItem><SelectItem value="block_contact">Block a specific contact</SelectItem></SelectContent></Select></div>
            {form.type === "block_contact" && (
              <div className="space-y-1.5"><Label>Blocked Contact</Label>
                <Select value={form.value} onValueChange={(v) => setForm({ ...form, value: v })}><SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent></Select></div>
            )}
            <div className="space-y-1.5"><Label>Reason *</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cs-exc-reason" /></div>
            <div className="space-y-1.5"><Label>Expires (optional)</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.user_id || !form.reason} className="bg-slate-900 hover:bg-slate-800" data-testid="cs-exc-save">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ChatSettings() {
  return (
    <div data-testid="chat-settings">
      <Tabs defaultValue="global">
        <TabsList className="mb-4">
          <TabsTrigger value="global" data-testid="cs-tab-global">Global</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="cs-tab-profiles">Role Profiles</TabsTrigger>
          <TabsTrigger value="policies" data-testid="cs-tab-policies">Access Policies</TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="cs-tab-exceptions">Individual</TabsTrigger>
        </TabsList>
        <TabsContent value="global"><Global /></TabsContent>
        <TabsContent value="profiles"><Profiles /></TabsContent>
        <TabsContent value="policies"><Policies /></TabsContent>
        <TabsContent value="exceptions"><Exceptions /></TabsContent>
      </Tabs>
    </div>
  );
}
